import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { setTenantIdGetter } from "@workspace/api-client-react";
import { METALUI_BRAND } from "@workspace/brand";
import { useMe } from "@/lib/auth";
import { toast } from "@/hooks/use-toast";

const STORAGE_KEY = "vc.selectedTenantId";
const MISMATCH_NOTIFIED_KEY = "vc.subdomainMismatchNotified";

const PLATFORM_DOMAIN = METALUI_BRAND.platformDomain;

function readStored(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore quota / private-mode errors */
  }
}

/**
 * Extract the tenant slug implied by the given hostname (defaults to the
 * current browser hostname).
 *
 * Returns a slug string when the host is `<slug>.metalui.co.uk` (a MetalUI
 * platform subdomain).
 *
 * Returns null for all other hosts (local dev, Replit preview, etc.).
 *
 * Accepts an optional `hostname` parameter so the function can be tested
 * without a DOM environment.
 */
export function resolveSubdomainSlug(hostname?: string): string | null {
  const raw =
    hostname ??
    (typeof window === "undefined" ? null : window.location.hostname);
  if (!raw) return null;
  const bare = raw.split(":")[0].toLowerCase();

  const suffix = `.${PLATFORM_DOMAIN}`;
  if (bare.endsWith(suffix)) {
    const slug = bare.slice(0, bare.length - suffix.length);
    if (slug && /^[a-z0-9-]+$/.test(slug)) {
      return slug;
    }
  }

  return null;
}

/**
 * Pure tenant-selection logic: given the list of active memberships, an
 * optional subdomain slug from the browser URL, and the currently-stored
 * tenant id, decide which tenant id to activate.
 *
 * Priority:
 *   1. Subdomain slug → the matching membership (if any).
 *   2. Currently-stored id → kept if it still belongs to the user. This is
 *      also what lets an explicit menu selection survive a reload.
 *   3. First membership → deterministic fallback.
 *
 * Returns `{ tenantId, changed }`:
 *   - `tenantId` — the id to activate (null only when `tenants` is empty).
 *   - `changed`  — true when the caller should write the new id to storage
 *                  and invalidate queries.
 *
 * Exported for unit testing; production callers use `TenantSelectionBridge`.
 */
export function pickTenantFromMemberships(
  tenants: ReadonlyArray<{ id: string; slug: string }>,
  subdomainSlug: string | null,
  stored: string | null,
): { tenantId: string | null; changed: boolean } {
  if (tenants.length === 0) {
    return { tenantId: stored, changed: false };
  }

  if (subdomainSlug) {
    const match = tenants.find((t) => t.slug === subdomainSlug);
    if (match) {
      return { tenantId: match.id, changed: stored !== match.id };
    }
    // User visited a subdomain they don't belong to; fall through to
    // the normal stored-id validation / first-tenant fallback below.
  }

  const valid = stored && tenants.some((t) => t.id === stored);
  if (valid) {
    return { tenantId: stored, changed: false };
  }

  return { tenantId: tenants[0].id, changed: true };
}

/**
 * Register the tenant-id getter as early as possible so the very first
 * business-data request (which can race ahead of /api/me) already carries
 * `X-Tenant-Id` from the user's last session. Routes mounted above
 * `requireTenant` (e.g. /api/me, /api/branding GET) don't care that the
 * header is set — the middleware never runs.
 *
 * This is a module-load side effect on purpose: it runs once when the app
 * bundle evaluates, before any React component renders. Subsequent calls
 * via the bridge below overwrite the getter to validate against the live
 * memberships list.
 */
setTenantIdGetter(() => readStored());

export function getSelectedTenantId(): string | null {
  return readStored();
}

export function setSelectedTenantId(id: string | null): void {
  writeStored(id);
}

/**
 * Bridge: after /api/me resolves, validate the stored tenant id
 * against the user's active memberships. If the stored id is missing or
 * stale (e.g. a membership was removed or suspended since last login), fall
 * back to the first membership and invalidate all queries so any boot-race
 * requests carrying the old tenant context are retried with the correct
 * header. The current stored id is read at effect time rather than captured
 * by React state, so a menu selection remains authoritative across a hard
 * reload.
 *
 * Subdomain auto-selection: when the browser hostname is `<slug>.metalui.co.uk`,
 * the matching tenant is resolved from the membership list by slug and
 * auto-selected — even when a different valid tenant ID is already stored.
 * This lets multi-workspace users land in the correct workspace automatically
 * without seeing the tenant picker.
 *
 * Single-tenant users never see the switcher; this still writes their
 * one id to localStorage so the next boot is header-correct.
 *
 * Zero-tenant (pending) users: backend will 403 `no_tenant` on every
 * business route — we leave the getter returning null so the existing
 * PendingApprovalScreen surfaces.
 */
export function TenantSelectionBridge(): null {
  const { me } = useMe();
  const qc = useQueryClient();

  useEffect(() => {
    if (!me) return;
    const tenants = me.tenants ?? [];
    if (tenants.length === 0) {
      // Two indistinguishable cases here from the FE's vantage point:
      //   (a) genuine pending user with zero memberships
      //   (b) /me swallowed a transient control-plane lookup error and
      //       returned tenants:[] to keep itself alive (see routes/users.ts
      //       catch path).
      // For (a) the backend will 403 `no_tenant` on every business route
      // regardless of what header we send, so a stale stored id is harmless.
      // For (b) clearing the stored id would actively break a previously-
      // working multi-tenant session until /me succeeds again. Doing
      // nothing is the conservative choice — let the existing stored
      // value ride. The next successful /me will either confirm it (valid
      // path → early return) or fall through and pick a fresh first
      // tenant.
      return;
    }

    const subdomainSlug = resolveSubdomainSlug();

    // When the subdomain doesn't match any membership, show a one-shot
    // banner so the user understands why they're seeing a different workspace,
    // then fall through to the normal stored-id validation / first-tenant
    // fallback via pickTenantFromMemberships below.
    if (subdomainSlug && !tenants.some((t) => t.slug === subdomainSlug)) {
      try {
        const notified = window.sessionStorage.getItem(MISMATCH_NOTIFIED_KEY);
        const notifiedSlugs: string[] = notified
          ? (JSON.parse(notified) as string[])
          : [];
        if (!notifiedSlugs.includes(subdomainSlug)) {
          toast({
            title: "Workspace not found",
            description: `You don't have access to the "${subdomainSlug}" workspace — showing your default workspace instead.`,
          });
          notifiedSlugs.push(subdomainSlug);
          window.sessionStorage.setItem(
            MISMATCH_NOTIFIED_KEY,
            JSON.stringify(notifiedSlugs),
          );
        }
      } catch {
        // sessionStorage unavailable (private mode, quota) — skip silently.
      }
    }

    // Read this after /me resolves. The bearer-auth browser fixture does not
    // have a Clerk user object, so the real UserMenu persists the selection
    // directly in localStorage before reloading the page.
    const stored = readStored();
    const { tenantId, changed } = pickTenantFromMemberships(
      tenants,
      subdomainSlug,
      stored,
    );
    if (changed && tenantId) {
      writeStored(tenantId);
      void qc.invalidateQueries();
    }
  }, [me, qc]);

  return null;
}
